import axios from 'axios';
import { handleAxiosAuthError } from '../utils/authErrorHandler';
/**
 * 面试记录管理服务
 * 负责面试记录的保存、读取和管理
 */

// 本地存储键名
const STORAGE_KEYS = {
  INTERVIEW_RECORDS: 'interview_records'
};

// 后端API实例
// 使用相对路径，通过Nginx代理访问后端API
const API_BASE_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '';
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000,
  headers: { 'Content-Type': 'application/json' }
});
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // 添加用户ID到请求头（兼容性）
  const user = localStorage.getItem('user');
  if (user) {
    try {
      const userData = JSON.parse(user);
      if (userData.id) {
        config.headers['X-User-Id'] = userData.id;
      }
    } catch (e) {
    }
  }
  
  return config;
});

// 响应拦截器 - 处理401错误
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // 使用统一的认证错误处理，自动刷新token并重试
      return handleAxiosAuthError(error);
    }
    return Promise.reject(error);
  }
);

/**
 * 面试记录API服务
 */
export class InterviewRecordAPI {
  
  /**
   * 保存面试记录
   * @param {Object} interviewRecord - 面试记录数据
   * @param {string} interviewRecord.type - 面试类型（职位名称）
   * @param {string} interviewRecord.interviewType - 面试类型（job/custom）
   * @param {string} interviewRecord.company - 公司名称
   * @param {string} interviewRecord.duration - 面试时长
   * @param {string} interviewRecord.date - 面试日期
   * @param {string} interviewRecord.time - 面试时间
   * @param {number} interviewRecord.score - 面试分数
   * @param {string} interviewRecord.status - 面试状态 (completed)
   * @param {boolean} interviewRecord.hasReport - 是否有报告
   * @param {boolean} interviewRecord.generating - 是否正在生成报告
   * @returns {Promise<Object>} 保存结果
   */
  static async saveInterviewRecord(interviewRecord) {
    try {
      // 获取现有记录
      const existingRecords = this.getInterviewRecords();
      
      // 生成唯一ID
      const newRecord = {
        id: Date.now(),
        ...interviewRecord,
        createdAt: new Date().toISOString()
      };
      
      // 添加到记录列表开头（最新的在前面）
      const updatedRecords = [newRecord, ...existingRecords];
      
      // 保存到本地存储
      localStorage.setItem(STORAGE_KEYS.INTERVIEW_RECORDS, JSON.stringify(updatedRecords));
      return {
        success: true,
        data: newRecord
      };
      
    } catch (error) {
      return {
        success: false,
        error: error.message || '保存失败'
      };
    }
  }
  
  static getInterviewRecords() {
    try {
      const records = localStorage.getItem(STORAGE_KEYS.INTERVIEW_RECORDS);
      if (records) {
        return JSON.parse(records);
      }
      return [];
    } catch (error) {
      return [];
    }
  }
  
  static async updateInterviewRecord(recordId, updates) {
    try {
      const records = this.getInterviewRecords();
      const recordIndex = records.findIndex(record => record.id === recordId);
      
      if (recordIndex === -1) {
        throw new Error('面试记录不存在');
      }
      
      records[recordIndex] = {
        ...records[recordIndex],
        ...updates,
        updatedAt: new Date().toISOString()
      };
      
      localStorage.setItem(STORAGE_KEYS.INTERVIEW_RECORDS, JSON.stringify(records));
      if (updates.status === 'completed' || updates.has_report === true || (updates.evaluation_data || updates.evaluationData)) {
        window.dispatchEvent(new CustomEvent('interviewCompleted', {
          detail: { recordId: recordId }
        }));
      }
      
      return {
        success: true,
        data: records[recordIndex]
      };
      
    } catch (error) {
      return {
        success: false,
        error: error.message || '更新失败'
      };
    }
  }
  
  static async deleteInterviewRecord(recordId) {
    try {
      const records = this.getInterviewRecords();
      const filteredRecords = records.filter(record => record.id !== recordId);
      
      if (filteredRecords.length === records.length) {
        throw new Error('面试记录不存在');
      }
      
      localStorage.setItem(STORAGE_KEYS.INTERVIEW_RECORDS, JSON.stringify(filteredRecords));
      return {
        success: true
      };
      
    } catch (error) {
      return {
        success: false,
        error: error.message || '删除失败'
      };
    }
  }
  
  static async clearAllRecords() {
    try {
      localStorage.removeItem(STORAGE_KEYS.INTERVIEW_RECORDS);
      return {
        success: true
      };
      
    } catch (error) {
      return {
        success: false,
        error: error.message || '清空失败'
      };
    }
  }

  // ---------------- 新增：后端CRUD ----------------

  // 轻量缓存与并发请求去重
  static _recordsCache = new Map(); // key -> { ts, data }
  static _inFlight = new Map(); // key -> Promise
  static _CACHE_TTL_MS = 8000; // 8s 短TTL，减少瞬时重复请求

  /**
   * 获取远程面试记录，带短TTL缓存与并发去重
   * @param {string|number} [userId] 可选用户ID；不传则从本地user推断并通过请求头传递
   * @param {{ forceRefresh?: boolean }} [options]
   */
  static async fetchInterviewRecordsRemote(userId, options = {}) {
    const { forceRefresh = false } = options;

    // 规范化key：优先使用参数，其次从localStorage中提取
    let effectiveUserId = userId;
    if (!effectiveUserId) {
      try {
        const userStr = localStorage.getItem('user');
        const parsed = userStr ? JSON.parse(userStr) : null;
        effectiveUserId = parsed?.id || parsed?.phone || 'auto';
      } catch {
        effectiveUserId = 'auto';
      }
    }
    const cacheKey = `records:${String(effectiveUserId)}`;

    // 使用缓存（短TTL）
    if (!forceRefresh && this._recordsCache.has(cacheKey)) {
      const cached = this._recordsCache.get(cacheKey);
      if (Date.now() - cached.ts < this._CACHE_TTL_MS) {
        return { success: true, data: cached.data };
      }
    }

    // 并发去重：复用正在进行的请求
    if (!forceRefresh && this._inFlight.has(cacheKey)) {
      try {
        const data = await this._inFlight.get(cacheKey);
        return { success: true, data };
      } catch (error) {
        return { success: false, error: error?.message || '获取失败' };
      }
    }

    // 真实请求
    const requestPromise = (async () => {
      const url = effectiveUserId && effectiveUserId !== 'auto'
        ? `/api/v1/mock-interview/records/?user_id=${encodeURIComponent(effectiveUserId)}`
        : '/api/v1/mock-interview/records/';
      const { data } = await api.get(url);
      // 写入缓存
      this._recordsCache.set(cacheKey, { ts: Date.now(), data });
      return data;
    })();

    this._inFlight.set(cacheKey, requestPromise);
    try {
      const data = await requestPromise;
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || error.message || '获取失败' };
    } finally {
      this._inFlight.delete(cacheKey);
    }
  }

  static async getInterviewRecordRemote(recordId) {
    try {
      const { data } = await api.get(`/api/v1/mock-interview/records/${recordId}`);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || error.message || '获取失败' };
    }
  }

  static async saveInterviewRecordRemote(interviewRecord) {
    try {
      const { data } = await api.post('/api/v1/mock-interview/records/', interviewRecord);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || error.message || '保存失败' };
    }
  }

  static async updateInterviewRecordRemote(recordId, updates) {
    try {
      const { data } = await api.patch(`/api/v1/mock-interview/records/${recordId}`, updates);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || error.message || '更新失败' };
    }
  }

  static async deleteInterviewRecordRemote(recordId) {
    try {
      await api.delete(`/api/v1/mock-interview/records/${recordId}`);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || error.message || '删除失败' };
    }
  }
  
  /**
   * 格式化面试时长
   * @param {number} seconds - 秒数
   * @returns {string} 格式化后的时长
   */
  static formatDuration(seconds) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    if (minutes === 0) {
      return `${remainingSeconds}秒`;
    } else if (remainingSeconds === 0) {
      return `${minutes}分钟`;
    } else {
      return `${minutes}分${remainingSeconds}秒`;
    }
  }
  
  /**
   * 格式化日期时间
   * @param {Date|string} date - 日期
   * @returns {Object} 格式化的日期和时间
   */
  static formatDateTime(date) {
    const dateObj = new Date(date);
    const dateStr = dateObj.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    const timeStr = dateObj.toLocaleTimeString('zh-CN', {
      hour: '2-digit',
      minute: '2-digit'
    });
    
    return {
      date: dateStr,
      time: timeStr
    };
  }
}